/**
 * Seed inicial del mock backend — todo el dominio.
 *
 * Espejo simplificado del seed del mockup vanilla. Datos suficientes para
 * validar los flujos críticos: 5 productos, 5 clientes, 9 remitos pendientes
 * en distintos estados (transferencia, venta, destrucción, multi-producto,
 * con/sin series pre-asignadas).
 */

import type { Usuario } from '@/api/contracts/usuarios';
import type { Ubicacion } from '@/api/contracts/ubicaciones';
import type { Producto } from '@/api/contracts/productos';
import type { Cliente } from '@/api/contracts/clientes';
import type { Serie } from '@/api/contracts/series';
import type { Remito } from '@/api/contracts/remitos';
import type { Operacion, Movimiento } from '@/api/contracts/operaciones';
import type { EventoAnmat } from '@/api/contracts/eventos-anmat';

// =============================================================================
// UBICACIONES
// =============================================================================

export const SEED_UBICACIONES: Ubicacion[] = [
  {
    id: 'everex',
    nombre: 'Everex Pharma',
    gln: '7791234500001',
    tipoActor: 'LABORATORIO',
    direccion: 'García de Cossio 6160, Capital Federal',
    cuit: '30-12345678-9',
  },
  {
    id: 'pharmatrans',
    nombre: 'Pharmatrans',
    gln: '7791234500002',
    tipoActor: 'OPERADOR LOGISTICO',
    direccion: 'Ruta 8 Km 42, Pilar',
    cuit: '30-87654321-0',
  },
  {
    id: 'lab_atlas',
    nombre: 'Pharma Atlas International',
    gln: '0301234500099',
    tipoActor: 'LABORATORIO',
    direccion: 'Frankfurt, DE',
    cuit: 'EXTRANJERO',
    externa: true,
  },
  {
    id: 'drog_suprema',
    nombre: 'Droguería Suprema SA',
    gln: '7791234500098',
    tipoActor: 'DROGUERIA',
    direccion: 'Av. Rivadavia 5000, CABA',
    cuit: '30-99887766-5',
    externa: true,
  },
];

// =============================================================================
// USUARIOS
// =============================================================================

// Emails corporativos del seed. Usados para matchear al login con Google
// (un email del Workspace de Everex → busca un Usuario con el mismo email).
// Laura (Pharmatrans) tiene email externo → en producción NO podrá usar Google
// SSO (solo password local) — paquete AB, modelo de coexistencia.
// PIN de firma electrónica (paquete AC) — 6 dígitos numéricos demo.
// En producción se hashean server-side; acá van en claro a propósito para
// que sea evidente que estamos en mock.
export const SEED_USUARIOS: Usuario[] = [
  {
    id: 'u1',
    username: 'christian',
    nombre: 'Christian Palazzo',
    iniciales: 'CP',
    email: 'christian.palazzo@everexpharma.com',
    rol: 'Administrador',
    pin: '123456',
    ubicacionId: 'everex',
    ubicacionDefaultId: 'everex',
    ubicacionesIds: ['everex', 'pharmatrans'],
  },
  {
    id: 'u2',
    username: 'laura',
    nombre: 'Laura Benítez',
    iniciales: 'LB',
    email: 'laura.benitez@pharmatrans.com.ar',
    rol: 'Operador',
    pin: '222233',
    ubicacionId: 'pharmatrans',
    ubicacionDefaultId: 'pharmatrans',
    ubicacionesIds: ['pharmatrans'],
  },
  {
    id: 'u3',
    username: 'pablo',
    nombre: 'Pablo Méndez',
    iniciales: 'PM',
    email: 'pablo.mendez@everexpharma.com',
    rol: 'Operador',
    pin: '334455',
    ubicacionId: 'pharmatrans',
    ubicacionDefaultId: 'pharmatrans',
    ubicacionesIds: ['everex', 'pharmatrans'],
  },
  {
    id: 'u4',
    username: 'martina',
    nombre: 'Martina Sosa',
    iniciales: 'MS',
    email: 'martina.sosa@everexpharma.com',
    rol: 'Gerente de Logística',
    pin: '445566',
    ubicacionId: 'everex',
    ubicacionDefaultId: 'everex',
    ubicacionesIds: ['everex', 'pharmatrans'],
  },
];

// =============================================================================
// PRODUCTOS (catálogo Everex)
// =============================================================================

export const SEED_PRODUCTOS: Producto[] = [
  { gtin: '07791234560011', nombre: 'BIRAT 250mg', presentacion: 'Comp. x 120 (Abiraterona acetato)' },
  { gtin: '07791234560028', nombre: 'MIZARID 25mg', presentacion: 'Cáps. x 21 (Lenalidomida)' },
  { gtin: '07791234560035', nombre: 'PIRFEX 267mg', presentacion: 'Cáps. x 270 (Pirfenidona)' },
  { gtin: '07791234560042', nombre: 'RIVAREX 20mg', presentacion: 'Comp. rec. x 30 (Rivaroxabán)' },
  { gtin: '07791234560059', nombre: 'BORTEZOMIB EVEREX 3.5mg', presentacion: 'F.A. x 1 (Bortezomib)' },
];

// =============================================================================
// CLIENTES
// =============================================================================

export const SEED_CLIENTES: Cliente[] = [
  {
    id: 'c1',
    descripcion: 'Farmacity S.A.',
    cuit: '30-11111111-1',
    domiciliosEntrega: [
      { id: 'c1-d1', descripcion: 'Sucursal Palermo', direccion: 'Av. Santa Fe 3456, CABA', gln: '7791000000001', tipoActor: 'FARMACIA', cuit: '30-11111111-1' },
      { id: 'c1-d2', descripcion: 'Sucursal Microcentro', direccion: 'Florida 700, CABA', gln: '7791000000002', tipoActor: 'FARMACIA', cuit: '30-11111111-1' },
      { id: 'c1-d3', descripcion: 'Depósito Central', direccion: 'Camino Real 100, Tigre', gln: '7791000000003', tipoActor: 'DROGUERIA', cuit: '30-11111111-1' },
    ],
  },
  {
    id: 'c2',
    descripcion: 'Dr. Ahorro S.A.',
    cuit: '30-22222222-2',
    domiciliosEntrega: [
      { id: 'c2-d1', descripcion: 'Casa Central', direccion: 'Av. Cabildo 2000, CABA', gln: '7791000000010', tipoActor: 'FARMACIA', cuit: '30-22222222-2' },
    ],
  },
  {
    id: 'c3',
    descripcion: 'Farmacias Del Plata',
    cuit: '30-33333333-3',
    domiciliosEntrega: [
      { id: 'c3-d1', descripcion: 'Sucursal Centro', direccion: 'Diagonal Norte 500, CABA', gln: '7791000000020', tipoActor: 'FARMACIA', cuit: '30-33333333-3' },
      { id: 'c3-d2', descripcion: 'Sucursal Recoleta', direccion: 'Av. Las Heras 2100, CABA', gln: '7791000000021', tipoActor: 'FARMACIA', cuit: '30-33333333-3' },
    ],
  },
  {
    id: 'c4',
    descripcion: 'Hospital Italiano',
    cuit: '30-44444444-4',
    domiciliosEntrega: [
      { id: 'c4-d1', descripcion: 'Sede Central', direccion: 'Gascón 450, CABA', gln: '7791000000030', tipoActor: 'ESTABLECIMIENTO ASISTENCIAL', cuit: '30-44444444-4' },
    ],
  },
  {
    id: 'c5',
    descripcion: 'Destrucciones Industriales S.A.',
    cuit: '30-55555555-5',
    esDestructor: true,
    domiciliosEntrega: [
      { id: 'c5-d1', descripcion: 'Planta de Tratamiento', direccion: 'Parque Industrial Pilar, Ruta 8 Km 50', gln: null, tipoActor: null, cuit: '30-55555555-5' },
    ],
  },
];

// =============================================================================
// SERIES (helper de generación)
// =============================================================================

function daysAgo(n: number): string {
  return new Date(Date.now() - n * 86400_000).toISOString();
}

let _serieSeq = 1000;
function mkSerie(
  gtin: string,
  lote: string,
  vencimiento: string,
  ubicacionId: string | null,
  tenencia: Serie['tenencia'],
  diasAtras: number,
): Serie {
  const id = `SN-2026-${String(_serieSeq++).padStart(7, '0')}`;
  return {
    id,
    gtin,
    lote,
    vencimiento,
    ubicacionActualId: ubicacionId,
    tenencia,
    createdAt: daysAgo(diasAtras),
  };
}

// Series en poder de Everex — algunas se pre-asignan a remitos demo
const birEverex: Serie[] = Array.from({ length: 60 }, () =>
  mkSerie('07791234560011', 'BIR-2026A', '08/2027', 'everex', 'en_poder', 60),
);
const mizEverex: Serie[] = Array.from({ length: 25 }, () =>
  mkSerie('07791234560028', 'MIZ-2026B', '03/2027', 'everex', 'en_poder', 55),
);
const pirEverex: Serie[] = Array.from({ length: 25 }, () =>
  mkSerie('07791234560035', 'PIR-2026C', '11/2026', 'everex', 'en_poder', 50),
);
const rivEverex: Serie[] = Array.from({ length: 15 }, () =>
  mkSerie('07791234560042', 'RIV-2026D', '06/2027', 'everex', 'en_poder', 45),
);
const borEverex: Serie[] = Array.from({ length: 15 }, () =>
  mkSerie('07791234560059', 'BOR-2026E', '10/2027', 'everex', 'en_poder', 40),
);

// Series en poder de Pharmatrans (ya recibidas)
const birPharma: Serie[] = Array.from({ length: 15 }, () =>
  mkSerie('07791234560011', 'BIR-2026A', '08/2027', 'pharmatrans', 'en_poder', 28),
);
const pirPharma: Serie[] = Array.from({ length: 15 }, () =>
  mkSerie('07791234560035', 'PIR-2026C', '11/2026', 'pharmatrans', 'en_poder', 28),
);

// En tránsito Everex → Pharmatrans (pendiente de recepción)
const enTransitoBir: Serie[] = Array.from({ length: 20 }, () =>
  mkSerie('07791234560011', 'BIR-2026A', '08/2027', 'everex', 'en_transito', 5),
);

export const SEED_SERIES: Serie[] = [
  ...birEverex,
  ...mizEverex,
  ...pirEverex,
  ...rivEverex,
  ...borEverex,
  ...birPharma,
  ...pirPharma,
  ...enTransitoBir,
];

// =============================================================================
// REMITOS PENDIENTES (entrada del ERP, sin trazar)
// =============================================================================

export const SEED_REMITOS: Remito[] = [
  // Transferencia Everex → Pharmatrans con series pre-asignadas (v9)
  {
    numero: '0002-00000001',
    codigoComprobante: null,
    origenUbicacionId: 'everex',
    destinoTipo: 'ubicacion_interna',
    destinoUbicacionId: 'pharmatrans',
    clienteId: null,
    domicilioEntregaId: null,
    fecha: daysAgo(1),
    items: [
      { gtin: '07791234560011', lote: 'BIR-2026A', cantidad: 10, seriesIds: birEverex.slice(0, 10).map((s) => s.id) },
      { gtin: '07791234560042', lote: 'RIV-2026D', cantidad: 5, seriesIds: rivEverex.slice(0, 5).map((s) => s.id) },
    ],
    estadoEnPlataforma: 'pendiente_trazar',
  },
  // Transferencia simple (sin series pre-asignadas)
  {
    numero: '0002-00000002',
    codigoComprobante: null,
    origenUbicacionId: 'everex',
    destinoTipo: 'ubicacion_interna',
    destinoUbicacionId: 'pharmatrans',
    clienteId: null,
    domicilioEntregaId: null,
    fecha: daysAgo(1),
    items: [{ gtin: '07791234560028', lote: 'MIZ-2026B', cantidad: 8 }],
    estadoEnPlataforma: 'pendiente_trazar',
  },
  // Destrucción
  {
    numero: '0003-00000001',
    codigoComprobante: null,
    origenUbicacionId: 'everex',
    destinoTipo: 'destruccion',
    destinoUbicacionId: null,
    clienteId: 'c5',
    domicilioEntregaId: 'c5-d1',
    fecha: daysAgo(0),
    items: [{ gtin: '07791234560035', lote: 'PIR-2026C', cantidad: 5 }],
    estadoEnPlataforma: 'pendiente_trazar',
  },
  // Venta multi-producto a farmacia
  {
    numero: '0004-00000001',
    codigoComprobante: null,
    origenUbicacionId: 'everex',
    destinoTipo: 'cliente_externo',
    destinoUbicacionId: null,
    clienteId: 'c1',
    domicilioEntregaId: 'c1-d1',
    fecha: daysAgo(0),
    items: [
      { gtin: '07791234560042', lote: 'RIV-2026D', cantidad: 5 },
      { gtin: '07791234560011', lote: 'BIR-2026A', cantidad: 5 },
    ],
    estadoEnPlataforma: 'pendiente_trazar',
  },
  // Venta a hospital con series pre-asignadas
  {
    numero: '0004-00000002',
    codigoComprobante: null,
    origenUbicacionId: 'everex',
    destinoTipo: 'cliente_externo',
    destinoUbicacionId: null,
    clienteId: 'c4',
    domicilioEntregaId: 'c4-d1',
    fecha: daysAgo(0),
    items: [
      { gtin: '07791234560059', lote: 'BOR-2026E', cantidad: 5, seriesIds: borEverex.slice(0, 5).map((s) => s.id) },
      { gtin: '07791234560028', lote: 'MIZ-2026B', cantidad: 3, seriesIds: mizEverex.slice(0, 3).map((s) => s.id) },
    ],
    estadoEnPlataforma: 'pendiente_trazar',
  },
  // Venta desde Pharmatrans
  {
    numero: '0001-00005430',
    codigoComprobante: null,
    origenUbicacionId: 'pharmatrans',
    destinoTipo: 'cliente_externo',
    destinoUbicacionId: null,
    clienteId: 'c3',
    domicilioEntregaId: 'c3-d1',
    fecha: daysAgo(0),
    items: [{ gtin: '07791234560011', lote: 'BIR-2026A', cantidad: 8 }],
    estadoEnPlataforma: 'pendiente_trazar',
  },
  // Venta desde Pharmatrans con series pre-asignadas
  {
    numero: '0001-00005432',
    codigoComprobante: null,
    origenUbicacionId: 'pharmatrans',
    destinoTipo: 'cliente_externo',
    destinoUbicacionId: null,
    clienteId: 'c3',
    domicilioEntregaId: 'c3-d1',
    fecha: daysAgo(0),
    items: [
      { gtin: '07791234560011', lote: 'BIR-2026A', cantidad: 5, seriesIds: birPharma.slice(0, 5).map((s) => s.id) },
    ],
    estadoEnPlataforma: 'pendiente_trazar',
  },
];

// =============================================================================
// OPERACIONES + MOVIMIENTOS PRE-EXISTENTES (transferencias en tránsito)
// =============================================================================
// Algunas operaciones ya informadas al SNT para que la vista Recepción tenga
// material desde el primer login (sin tener que hacer un despacho primero).

const opTransitoEverexPharma = 'OP-SEED01';
const codTxSeed1 = 'TX-SEED1-EV2PH';

export const SEED_OPERACIONES: Operacion[] = [
  {
    id: opTransitoEverexPharma,
    tipo: 'despacho_transferencia',
    ubicacionId: 'everex',
    destinoUbicacionId: 'pharmatrans',
    remitoNumero: null,
    eventoAnmatId: 131, // LAB → OPERADOR LOGISTICO
    fecha: daysAgo(5),
    usuarioId: 'u1',
    seriesIds: enTransitoBir.map((s) => s.id),
    estado: 'cerrada',
    anmat: {
      tipoMensaje: 'informarTransaccion',
      estado: 'informada',
      intentos: 1,
      codigoTransaccion: codTxSeed1,
      mensajes: [
        { ts: daysAgo(5), tipo: 'request', body: 'informarTransaccion enviado al SNT' },
        { ts: daysAgo(5), tipo: 'response', body: `OK · ${codTxSeed1}` },
      ],
    },
    pdfGeneradoAt: null,
  },
];

export const SEED_MOVIMIENTOS: Movimiento[] = enTransitoBir.map((s, i) => ({
  id: `MOV-SEED${String(i).padStart(3, '0')}`,
  serieId: s.id,
  operacionId: opTransitoEverexPharma,
  tipoEvento: 'despacho_transferencia',
  fecha: daysAgo(5),
  ubicacionId: 'everex',
  usuarioId: 'u1',
  codigoTransaccion: codTxSeed1,
  eventoAnmatId: 131,
  detalle: 'Despacho histórico Everex → Pharmatrans (semilla)',
}));

// =============================================================================
// CATÁLOGO ANMAT — subset (~30 eventos clave)
// =============================================================================
// El catálogo completo tiene 524 entradas. Acá cargamos solo lo necesario para
// que los flujos del seed funcionen end-to-end. Producción carga el CSV oficial.

export const SEED_EVENTOS_ANMAT: EventoAnmat[] = [
  // LABORATORIO emisor → eslabón posterior
  { id: 128, evento: 'DISTRIBUCION DEL PRODUCTO A UN ESLABON POSTERIOR', informador: 'LABORATORIO', origen: 'LABORATORIO', destino: 'DROGUERIA' },
  { id: 129, evento: 'DISTRIBUCION DEL PRODUCTO A UN ESLABON POSTERIOR', informador: 'LABORATORIO', origen: 'LABORATORIO', destino: 'ESTABLECIMIENTO ASISTENCIAL' },
  { id: 130, evento: 'DISTRIBUCION DEL PRODUCTO A UN ESLABON POSTERIOR', informador: 'LABORATORIO', origen: 'LABORATORIO', destino: 'FARMACIA' },
  { id: 131, evento: 'DISTRIBUCION DEL PRODUCTO A UN ESLABON POSTERIOR', informador: 'LABORATORIO', origen: 'LABORATORIO', destino: 'OPERADOR LOGISTICO' },
  { id: 132, evento: 'DISTRIBUCION DEL PRODUCTO A UN ESLABON POSTERIOR', informador: 'LABORATORIO', origen: 'LABORATORIO', destino: 'LAB. DE MEZCLA INTRAVENOSA' },
  { id: 133, evento: 'DISTRIBUCION DEL PRODUCTO A UN ESLABON POSTERIOR', informador: 'LABORATORIO', origen: 'LABORATORIO', destino: 'DISTRIBUIDORA' },
  // Eventos terminales LABORATORIO
  { id: 145, evento: 'CODIGO O PRODUCTO DETERIORADO/DESTRUIDO', informador: 'LABORATORIO', origen: 'LABORATORIO', destino: null },
  { id: 146, evento: 'PRODUCTO ROBADO O EXTRAVIADO', informador: 'LABORATORIO', origen: 'LABORATORIO', destino: null },
  { id: 147, evento: 'PRODUCTO VENCIDO', informador: 'LABORATORIO', origen: 'LABORATORIO', destino: null },

  // OPERADOR LOGISTICO emisor → eslabón posterior
  { id: 230, evento: 'DISTRIBUCION DEL PRODUCTO A UN ESLABON POSTERIOR', informador: 'OPERADOR LOGISTICO', origen: 'OPERADOR LOGISTICO', destino: 'FARMACIA' },
  { id: 231, evento: 'DISTRIBUCION DEL PRODUCTO A UN ESLABON POSTERIOR', informador: 'OPERADOR LOGISTICO', origen: 'OPERADOR LOGISTICO', destino: 'DROGUERIA' },
  { id: 232, evento: 'DISTRIBUCION DEL PRODUCTO A UN ESLABON POSTERIOR', informador: 'OPERADOR LOGISTICO', origen: 'OPERADOR LOGISTICO', destino: 'ESTABLECIMIENTO ASISTENCIAL' },
  { id: 240, evento: 'CODIGO O PRODUCTO DETERIORADO/DESTRUIDO', informador: 'OPERADOR LOGISTICO', origen: 'OPERADOR LOGISTICO', destino: null },

  // LABORATORIO receptor — recepción normal
  { id: 250, evento: 'RECEPCION DE PRODUCTO DESDE UN ESLABON ANTERIOR', informador: 'LABORATORIO', origen: 'OPERADOR LOGISTICO', destino: 'LABORATORIO' },
  { id: 251, evento: 'RECEPCION DE PRODUCTO DESDE UN ESLABON ANTERIOR', informador: 'LABORATORIO', origen: 'LABORATORIO', destino: 'LABORATORIO' },
  // LABORATORIO receptor — devolución (caso CR-5, fallback cuando no hay recepción normal)
  { id: 139, evento: 'RECEPCION EN CARACTER DE DEVOLUCION', informador: 'LABORATORIO', origen: 'DROGUERIA', destino: 'LABORATORIO' },
  { id: 140, evento: 'RECEPCION EN CARACTER DE DEVOLUCION', informador: 'LABORATORIO', origen: 'FARMACIA', destino: 'LABORATORIO' },

  // OPERADOR LOGISTICO receptor
  { id: 260, evento: 'RECEPCION DE PRODUCTO DESDE UN ESLABON ANTERIOR', informador: 'OPERADOR LOGISTICO', origen: 'LABORATORIO', destino: 'OPERADOR LOGISTICO' },
  { id: 261, evento: 'RECEPCION DE PRODUCTO DESDE UN ESLABON ANTERIOR', informador: 'OPERADOR LOGISTICO', origen: 'DROGUERIA', destino: 'OPERADOR LOGISTICO' },
  { id: 262, evento: 'RECEPCION DE PRODUCTO DESDE UN ESLABON ANTERIOR', informador: 'OPERADOR LOGISTICO', origen: 'OPERADOR LOGISTICO', destino: 'OPERADOR LOGISTICO' },
];
